import { IconArrowRight, IconMapPin, IconPhone } from '@tabler/icons-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { PosterLogo, PosterSheet, fs, numberScale, posterFields, valueFitScale } from '../posterParts';

/**
 * "Magazine" — a quiet editorial page: thin rules and a masthead line, the organisation as a big serif headline, a giant vacancy number
 * beside two hairline-ruled facts, the two dates as large numerals between rules, a black call-to-action bar and a minimal footer.
 * Palette: paper, ink, muted, accent, rule.
 */
const SERIF = "'Noto Serif Bengali', 'Tiro Bangla', serif";
const SANS = "'Noto Sans Bengali', 'Anek Bangla', sans-serif";

function Fact({ p, label, children }) {
    return (
        <div style={{ borderTop: `0.3cqw solid ${p.rule}`, paddingTop: fs(1.6), paddingBottom: fs(1.8) }}>
            <div style={{ color: p.accent, fontFamily: SANS, fontSize: fs(2.5), fontWeight: 800, letterSpacing: '0.03em', lineHeight: 1.2 }}>{label}</div>
            <div style={{ marginTop: fs(0.8) }}>{children}</div>
        </div>
    );
}

function DateBlock({ p, label, parts, last = false }) {
    return (
        <div className="flex-1" style={{ padding: `${fs(1.8)} ${fs(3)}`, borderRight: last ? 'none' : `0.3cqw solid ${p.rule}` }}>
            <div style={{ color: p.accent, fontFamily: SANS, fontSize: fs(2.5), fontWeight: 800, letterSpacing: '0.03em' }}>{label}</div>
            <div className="flex items-baseline" style={{ gap: fs(1.6), marginTop: fs(0.6) }}>
                <div style={{ color: p.ink, fontSize: fs(11), fontWeight: 800, lineHeight: 1.05 }}>{parts.day}</div>
                <div style={{ color: p.ink, fontSize: fs(4.6), fontWeight: 600, lineHeight: 1.1 }}>{parts.month}</div>
            </div>
        </div>
    );
}

export default function MagazineDesign({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    const p = theme.palette;
    const f = posterFields(job, business, t);
    const eduScale = valueFitScale(f.edu, 34);
    const ageScale = valueFitScale(f.age, 26);

    const body = (
        <div className="flex flex-1 flex-col justify-center" style={{ padding: `${fs(5)} 7cqw ${fs(2.6)}`, gap: fs(2.6) }}>
            {/* masthead */}
            <div>
                <div style={{ height: '0.9cqw', background: p.ink }} />
                <div className="flex items-center justify-between" style={{ padding: `${fs(1.3)} 0`, fontFamily: SANS, color: p.ink, fontSize: fs(2.6), fontWeight: 800, letterSpacing: '0.04em' }}>
                    <div>{f.label.notice}</div>
                    <div style={{ color: p.muted, fontWeight: 700, letterSpacing: '0.02em' }}>{f.label.end} · {f.end}</div>
                </div>
                <div style={{ height: '0.25cqw', background: p.ink }} />
            </div>

            {/* headline */}
            <div className="flex items-start" style={{ gap: fs(3.2) }}>
                <div className="min-w-0 flex-1">
                    <div style={{ width: fs(12), height: '1cqw', background: p.accent, marginBottom: fs(2) }} />
                    <div style={{ color: p.ink, fontSize: fs(8), fontWeight: 900, lineHeight: 1.1 }}>{f.org}</div>
                    {f.position && <div style={{ color: p.muted, fontFamily: "'Tiro Bangla', 'Noto Serif Bengali', serif", fontStyle: 'italic', fontSize: fs(4), lineHeight: 1.3, marginTop: fs(1.6) }}>{f.position}</div>}
                </div>
                <PosterLogo job={job} size={15} style={{ border: `0.3cqw solid ${p.rule}`, padding: fs(1.2), background: '#fff' }} />
            </div>

            {/* the number + two facts */}
            <div className="grid items-end" style={{ gridTemplateColumns: '1.02fr 1fr', gap: fs(4), paddingTop: fs(1) }}>
                <div style={{ position: 'relative' }}>
                    <div style={{ color: p.accent, fontFamily: SANS, fontSize: fs(2.6), fontWeight: 800, letterSpacing: '0.03em' }}>{f.label.vacancy}</div>
                    <div className="flex items-end" style={{ gap: fs(1.4) }}>
                        <div style={{ color: p.accent, fontSize: `calc(${30 * numberScale(f.vacancy)}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 0.95, letterSpacing: '-0.02em' }}>{f.vacancy}</div>
                        <div style={{ color: p.ink, fontSize: fs(5), fontWeight: 800, paddingBottom: fs(2.2) }}>{f.label.unit}</div>
                    </div>
                </div>
                <div>
                    <Fact p={p} label={f.label.edu}>
                        <div style={{ color: p.ink, fontSize: `calc(${3.9 * eduScale}cqw * var(--fs, 1))`, fontWeight: 700, lineHeight: 1.3 }}>{f.edu}</div>
                    </Fact>
                    <Fact p={p} label={f.label.age}>
                        <div style={{ color: p.ink, fontSize: `calc(${3.9 * ageScale}cqw * var(--fs, 1))`, fontWeight: 700, lineHeight: 1.3 }}>{f.age}</div>
                    </Fact>
                </div>
            </div>

            {/* dates */}
            <div className="flex" style={{ borderTop: `0.3cqw solid ${p.rule}`, borderBottom: `0.3cqw solid ${p.rule}` }}>
                <DateBlock p={p} label={f.label.start} parts={f.startParts} />
                <DateBlock p={p} label={f.label.end} parts={f.endParts} last />
            </div>

            <div style={{ color: p.muted, fontStyle: 'italic', fontSize: fs(3), lineHeight: 1.35, textAlign: 'center' }}>“{f.label.promo}”</div>

            <div className="flex items-center justify-between" style={{ background: p.ink, color: '#fff', padding: `${fs(2.2)} ${fs(3.4)}`, fontFamily: SANS }}>
                <div style={{ fontSize: fs(4.2), fontWeight: 800 }}>{f.label.cta}</div>
                <IconArrowRight className="flex-none" style={{ width: fs(5.4), height: fs(5.4), color: p.accentLight }} stroke={2.6} />
            </div>
        </div>
    );

    const footer = showFooter ? (
        <div style={{ margin: `0 7cqw ${fs(4.6)}`, borderTop: `0.3cqw solid ${p.rule}`, paddingTop: fs(2.2), fontFamily: SANS }}>
            <div className="flex items-center" style={{ gap: fs(2.4) }}>
                {f.shop.logo && <img src={f.shop.logo} alt="" className="flex-none object-contain" style={{ width: fs(9), height: fs(9), border: `0.3cqw solid ${p.rule}`, padding: '0.5cqw', background: '#fff' }} />}
                <div className="min-w-0 flex-1">
                    <div style={{ color: p.ink, fontFamily: SERIF, fontSize: fs(5.4), fontWeight: 900, lineHeight: 1.14, opacity: f.shop.placeholder ? 0.6 : 1 }}>{f.shop.nameShown}</div>
                    <div className="flex items-start" style={{ gap: fs(1), marginTop: fs(0.8), color: p.muted, fontSize: fs(3.2), lineHeight: 1.3, opacity: f.shop.addressPlaceholder ? 0.6 : 1 }}>
                        <IconMapPin className="flex-none" style={{ width: fs(3.4), height: fs(3.4), color: p.accent, marginTop: '0.2cqw' }} />
                        <div>{f.shop.addressShown}</div>
                    </div>
                </div>
                {f.shop.phone && (
                    <div className="flex flex-none items-center" style={{ gap: fs(1), color: p.ink, fontSize: fs(4.2), fontWeight: 800 }}>
                        <IconPhone className="flex-none" style={{ width: fs(4), height: fs(4), color: p.accent }} />
                        <div>{f.shop.phone}</div>
                    </div>
                )}
            </div>
        </div>
    ) : null;

    return (
        <PosterSheet
            deps={[job, business, theme, showFooter, t]}
            className={className}
            fontFamily={SERIF}
            background={p.paper}
            body={body}
            footer={footer}
        />
    );
}
